import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Panel } from "@/components/Panel";
import { toast } from "sonner";
import { Key, Save, Eye, EyeOff, Trash2, RefreshCw, ShieldCheck, AlertTriangle, ExternalLink } from "lucide-react";

// Phase 36.23 — Platform Keys vault UI.
// Lets the super-admin paste integration secrets into an encrypted-at-rest
// MongoDB vault from inside DARA, without needing Emergent's Custom Keys
// panel (which currently doesn't surface .env-added vars).

const KEY_DOCS = {
  SQUARE_APP_ID: {
    label: "Square · Application ID",
    hint: "From Square Developer → your app → OAuth tab. Sandbox starts with 'sandbox-sq0idb-'.",
    masked: false,
  },
  SQUARE_APP_SECRET: {
    label: "Square · Application Secret",
    hint: "From Square Developer → OAuth tab → click 'Show'. Sandbox starts with 'sandbox-sq0csb-'. Treat like a password.",
    masked: true,
  },
  SQUARE_WEBHOOK_SIGNATURE_KEY: {
    label: "Square · Webhook Signature Key",
    hint: "From Square Developer → Webhooks → Subscriptions → your subscription. Starts with 'wsec_'.",
    masked: true,
  },
  SQUARE_ENV: {
    label: "Square · Environment",
    hint: "Either 'sandbox' (testing) or 'production' (live merchants).",
    masked: false,
  },
};

export default function PlatformKeysTab() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [draft, setDraft] = useState({});
  const [reveal, setReveal] = useState({});
  const [saving, setSaving] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/secrets/status");
      setItems(r.data.items || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load keys");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const save = async (key) => {
    const value = (draft[key] || "").trim();
    if (!value) {
      toast.error("Paste a value first");
      return;
    }
    setSaving(key);
    try {
      await api.post("/admin/secrets/set", { key, value });
      toast.success(`${key} saved`);
      setDraft(prev => ({ ...prev, [key]: "" }));
      setReveal(prev => ({ ...prev, [key]: false }));
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(null);
    }
  };

  const wipe = async (key) => {
    if (!confirm(`Delete ${key} from the encrypted vault? Backend will fall back to .env value if any.`)) return;
    try {
      await api.delete(`/admin/secrets/${key}`);
      toast.success(`${key} wiped from vault`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    }
  };

  return (
    <div className="space-y-4" data-testid="platform-keys-tab">
      <Panel title="// PLATFORM KEYS · ENCRYPTED VAULT" testId="platform-keys-card">
        <div className="p-5 space-y-3">
          <div className="flex items-start gap-3 border border-[#FFB000]/30 bg-[#FFB000]/5 p-3">
            <ShieldCheck className="w-5 h-5 text-[#FFB000] flex-shrink-0 mt-0.5" />
            <div className="text-xs text-zinc-300 leading-relaxed">
              <span className="text-[#FFB000] font-bold">Encrypted at rest with AES-256.</span> Values entered here are encrypted with a key derived from your <code className="text-[#FFB000]">JWT_SECRET</code> and persisted to MongoDB. They never appear in logs, never echo back in API responses, and never leave your environment.
            </div>
          </div>

          {loading && <div className="font-mono text-xs text-zinc-600 p-4">LOADING…</div>}

          {!loading && items.map(item => {
            const doc = KEY_DOCS[item.key] || { label: item.key, hint: "", masked: true };
            const inputType = doc.masked && !reveal[item.key] ? "password" : "text";
            return (
              <div key={item.key} className="border border-zinc-900 bg-black p-4" data-testid={`secret-row-${item.key}`}>
                <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
                  <div className="flex items-center gap-2">
                    <Key className="w-3.5 h-3.5 text-zinc-500" />
                    <span className="font-heading font-bold text-sm">{doc.label}</span>
                    {item.ready ? (
                      <span className="px-2 py-0.5 border border-[#00E676]/40 text-[#00E676] bg-[#00E676]/5 font-mono text-[9px] uppercase tracking-widest">
                        ✓ {item.vault_set ? "VAULT" : "ENV"} · {item.effective_chars} CHARS
                      </span>
                    ) : (
                      <span className="px-2 py-0.5 border border-[#FF6B47]/40 text-[#FF6B47] bg-[#FF6B47]/5 font-mono text-[9px] uppercase tracking-widest">
                        ⚠ NOT SET
                      </span>
                    )}
                  </div>
                  {item.vault_set && (
                    <button
                      onClick={() => wipe(item.key)}
                      data-testid={`wipe-${item.key}`}
                      className="border border-zinc-800 hover:border-[#FF3B30] hover:text-[#FF3B30] text-zinc-500 px-2 py-1 font-mono text-[9px] uppercase tracking-widest flex items-center gap-1"
                    >
                      <Trash2 className="w-3 h-3" />
                      WIPE
                    </button>
                  )}
                </div>
                <p className="font-mono text-[10px] text-zinc-600 mb-3 leading-relaxed">{doc.hint}</p>
                <div className="flex items-center gap-2">
                  <input
                    type={inputType}
                    value={draft[item.key] || ""}
                    onChange={(e) => setDraft(prev => ({ ...prev, [item.key]: e.target.value }))}
                    placeholder={item.ready ? "Paste new value to replace…" : "Paste value here…"}
                    autoComplete="off"
                    spellCheck="false"
                    data-testid={`input-${item.key}`}
                    className="flex-1 bg-black border border-zinc-800 px-3 py-2 font-mono text-xs text-white focus:outline-none focus:border-[#FFB000]"
                  />
                  {doc.masked && (
                    <button
                      onClick={() => setReveal(prev => ({ ...prev, [item.key]: !prev[item.key] }))}
                      data-testid={`reveal-${item.key}`}
                      title={reveal[item.key] ? "Hide" : "Show"}
                      className="border border-zinc-800 hover:border-zinc-700 text-zinc-500 hover:text-white px-2 py-2"
                    >
                      {reveal[item.key] ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
                    </button>
                  )}
                  <button
                    onClick={() => save(item.key)}
                    disabled={saving === item.key || !(draft[item.key] || "").trim()}
                    data-testid={`save-${item.key}`}
                    className="bg-[#FFB000] text-black font-bold font-mono text-[10px] uppercase tracking-widest px-3 py-2 hover:opacity-90 disabled:opacity-40 flex items-center gap-1"
                  >
                    <Save className="w-3 h-3" />
                    {saving === item.key ? "SAVING…" : "SAVE"}
                  </button>
                </div>
                {item.vault_updated_at && (
                  <div className="font-mono text-[9px] text-zinc-700 mt-2">
                    Last updated · {new Date(item.vault_updated_at).toLocaleString()} · by {item.vault_updated_by || "—"}
                  </div>
                )}
              </div>
            );
          })}

          <button
            onClick={load}
            data-testid="refresh-keys"
            className="border border-zinc-800 hover:border-zinc-700 px-3 py-2 font-mono text-[10px] uppercase tracking-widest text-zinc-300 flex items-center gap-2"
          >
            <RefreshCw className="w-3.5 h-3.5" />
            REFRESH
          </button>
        </div>
      </Panel>

      <Panel title="// SQUARE INTEGRATION · QUICK LINKS" testId="square-quick-links">
        <div className="p-5 space-y-2 font-mono text-[11px] text-zinc-400">
          <a href="https://developer.squareup.com/apps" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 hover:text-[#FFB000]">
            <ExternalLink className="w-3 h-3" /> developer.squareup.com/apps — your Square applications
          </a>
          <a href="https://developer.squareup.com/docs/oauth-api/overview" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 hover:text-[#FFB000]">
            <ExternalLink className="w-3 h-3" /> OAuth flow documentation
          </a>
          <a href="https://developer.squareup.com/docs/webhooks/overview" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 hover:text-[#FFB000]">
            <ExternalLink className="w-3 h-3" /> Webhook subscription guide
          </a>
        </div>
      </Panel>
    </div>
  );
}
